import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, FileText, Loader2, Store,
  Calendar, ChevronRight, Clock, CheckCircle2, ExternalLink, Search,
} from 'lucide-react';

const formatSEK = (n) =>
  new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', minimumFractionDigits: 0 }).format(Number(n) || 0);
const formatDate = (d) => d ? new Date(d).toLocaleDateString('sv-SE') : '–';

/** API kan returnera leveransadress som sträng eller som objekt (Mongo / OrderCreate). */
const formatAddressForDisplay = (addr) => {
  if (addr == null || addr === '') return '';
  if (typeof addr === 'string') return addr.trim();
  if (typeof addr !== 'object') return String(addr);
  const o = addr;
  const zip = o.postal_code || o.postcode || o.zip || '';
  const city = o.city || o.town || '';
  const zipCity = [zip, city].filter(Boolean).join(' ').trim();
  const lines = [
    o.street || o.address || o.line1,
    zipCity,
    o.country,
    o.name,
    o.company,
  ].filter((x) => x != null && String(x).trim());
  return lines.join('\n').trim();
};

/** Aggregera radnivå till en rad per order (bokföringsvy). */
const aggregateDetailLinesByOrder = (lines) => {
  const map = new Map();
  for (const ln of lines || []) {
    const oid = String(ln.order_id ?? ln.order_number ?? '');
    if (!map.has(oid)) {
      map.set(oid, {
        order_id: oid,
        order_label: ln.order_number || oid,
        date: ln.date || '',
        line_ex_vat: 0,
        line_incl: 0,
        provision_varor: 0,
        provision_inkl_varor: 0,
        moms_provision_varor: 0,
        frakt: 0,
        expedition: 0,
        vat_goods: 0,
        vat_frakt: 0,
        vat_exp: 0,
        utbetalning_rad: 0,
        titles: [],
      });
    }
    const o = map.get(oid);
    const tit = (ln.title || '').trim();
    if (tit && !o.titles.includes(tit)) o.titles.push(tit);
    const provEx = Number(ln.provision_varor) || 0;
    const provInkl = ln.provision_inkl_varor != null
      ? Number(ln.provision_inkl_varor)
      : Math.round(provEx * 1.25 * 100) / 100;
    const momsP = ln.moms_provision_varor != null
      ? Number(ln.moms_provision_varor)
      : Math.round((provInkl - provEx) * 100) / 100;
    o.line_ex_vat += Number(ln.line_ex_vat) || 0;
    o.line_incl += Number(ln.line_total_incl_vat) || 0;
    o.provision_varor += provEx;
    o.provision_inkl_varor += provInkl;
    o.moms_provision_varor += momsP;
    o.frakt += Number(ln.allocated_shipping_incl_vat) || 0;
    o.expedition += Number(ln.allocated_handling_incl_vat) || 0;
    o.vat_goods += Number(ln.vat_goods) || 0;
    o.vat_frakt += Number(ln.vat_shipping) || 0;
    o.vat_exp += Number(ln.vat_handling) || 0;
    o.utbetalning_rad += Number(ln.utbetalning_rad) || 0;
  }
  return Array.from(map.values()).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
};

const filterInvoiceOrderRows = (rows, query, orderBuyers = null) => {
  const s = (query || '').trim().toLowerCase();
  if (!s) return rows ?? [];
  return (rows ?? []).filter((r) => {
    const b = orderBuyers?.[r.order_id];
    const parts = [
      r.order_label,
      r.order_id,
      r.date,
      String(r.line_incl ?? ''),
      String(r.utbetalning_rad ?? ''),
      ...((r.titles) || []),
      b?.buyer_name,
      b?.buyer_email,
    ];
    return parts.some((p) => String(p || '').toLowerCase().includes(s));
  });
};

const filterPeriodOrderRows = (rows, query) => {
  const s = (query || '').trim().toLowerCase();
  if (!s) return rows ?? [];
  return (rows ?? []).filter((r) => {
    const parts = [
      r.order_label,
      r.order_id,
      r.date,
      r.buyer_name,
      r.buyer_email,
      r.buyer_phone,
      r.sellers_label,
      String(r.line_incl ?? ''),
      String(r.utbetalning_rad ?? ''),
      ...((r.titles) || []),
    ];
    return parts.some((p) => String(p || '').toLowerCase().includes(s));
  });
};

const OrderTransactionDialog = ({ orderId, open, onOpenChange }) => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !orderId) {
      setPayload(null);
      return undefined;
    }
    let cancel = false;
    setLoading(true);
    setPayload(null);
    adminApi
      .getInvoiceOrderContext(orderId)
      .then((res) => {
        if (!cancel) setPayload(res.data);
      })
      .catch(() => {
        if (!cancel) {
          toast.error('Kunde inte hämta orderinformation');
          setPayload(null);
        }
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [open, orderId]);

  const ord = payload?.order;
  const buyer = payload?.buyer;
  const lines = payload?.lines || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Orderbekräftelse / transaktion</DialogTitle>
          <DialogDescription>
            {ord?.order_number || orderId}
            {ord?.created_at ? ` · ${formatDate(ord.created_at)}` : ''}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-[#003B73]" />
          </div>
        ) : !payload ? (
          <p className="text-sm text-gray-500">Ingen data.</p>
        ) : (
          <div className="space-y-5 text-sm">
            <section className="rounded-lg border border-gray-200 p-4 space-y-2">
              <h4 className="text-xs font-bold uppercase text-gray-500">Order</h4>
              <p className="text-gray-700">
                <span className="text-gray-500">Ordernr:</span>{' '}
                <span className="font-mono">{ord?.order_number || '–'}</span>
              </p>
              <p className="text-gray-700">
                <span className="text-gray-500">Status:</span> {ord?.status || '–'} ·{' '}
                <span className="text-gray-500">Betalning:</span> {ord?.payment_status || '–'}
              </p>
              <p className="text-gray-700">
                <span className="text-gray-500">Totalsumma:</span>{' '}
                {formatSEK(ord?.grand_total ?? ord?.total)}
              </p>
              {formatAddressForDisplay(ord?.shipping_address) ? (
                <p className="text-gray-700 whitespace-pre-wrap">
                  <span className="text-gray-500">Leveransadress:</span>{' '}
                  {formatAddressForDisplay(ord.shipping_address)}
                </p>
              ) : null}
              {ord?.delivery_method === 'pickup' ? (
                <p className="text-sm text-blue-800">Leveranssätt: Hämtning i butik</p>
              ) : null}
            </section>
            <section className="rounded-lg border border-gray-200 p-4 space-y-2">
              <h4 className="text-xs font-bold uppercase text-gray-500">Köpare</h4>
              <p className="text-gray-900 font-medium">{buyer?.name || '–'}</p>
              <p className="text-gray-700 break-all">{buyer?.email || '–'}</p>
              {buyer?.phone ? <p className="text-gray-700">{buyer.phone}</p> : null}
            </section>
            <section className="space-y-3">
              <h4 className="text-xs font-bold uppercase text-gray-500">Rader · annons & säljare</h4>
              <ul className="space-y-3">
                {lines.map((ln, idx) => (
                  <li
                    key={`${ln.product_id || 'x'}-${idx}`}
                    className="rounded-lg border border-gray-100 bg-gray-50/80 p-3 space-y-2"
                  >
                    <p className="font-medium text-gray-900">{ln.title}</p>
                    <p className="text-gray-600">
                      {ln.quantity} st × {formatSEK(ln.unit_price_incl_vat)} = {formatSEK(ln.line_total_incl_vat)}
                    </p>
                    <div className="text-gray-700 text-xs space-y-1">
                      <p><span className="text-gray-500">Säljare:</span> {ln.store_name}</p>
                      {ln.store_location ? (
                        <p><span className="text-gray-500">Ort:</span> {ln.store_location}</p>
                      ) : null}
                      {ln.store_org_number ? (
                        <p><span className="text-gray-500">Org.nr:</span> {ln.store_org_number}</p>
                      ) : null}
                      {ln.store_contact_email ? (
                        <p><span className="text-gray-500">Butik e-post:</span> {ln.store_contact_email}</p>
                      ) : null}
                    </div>
                    {ln.product_path ? (
                      <a
                        href={ln.product_path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#003B73] font-medium text-sm hover:underline"
                      >
                        Öppna annons <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** Minimal vy: endast bokföring/utbetalning enligt spec */
const SimpleLedgerSummary = ({ ctx }) => {
  const pEx = Number(ctx.provision_varor ?? ctx.total_provision_varor ?? ctx.commission) || 0;
  const pInkl = Number(ctx.provision_inkl_varor ?? ctx.total_provision_inkl_varor) || Math.round(pEx * 1.25 * 100) / 100;
  const fr = Number(ctx.frakt_intakter ?? ctx.total_frakt_intakter) || 0;
  const ex = Number(ctx.expeditions_intakter ?? ctx.total_expeditions_intakter) || 0;
  const bfTot = Number(ctx.total_provision_bf) || (pInkl + fr + ex);
  const bfMoms = Number(ctx.moms_byggfynd ?? ctx.total_moms_byggfynd_redovisning) || 0;
  const orderMoms = Number(ctx.moms_redovisning ?? ctx.total_vat) || 0;
  const incl = Number(ctx.total_sales) || 0;
  const utb = Number(ctx.utbetalning_till_butik ?? ctx.total_utbetalning_butiker) || 0;

  const L = ({ children }) => <div className="flex justify-between gap-3 text-sm py-0.5">{children}</div>;
  const Lab = ({ c }) => <span className="text-gray-600">{c}</span>;
  const Val = ({ c, b }) => <span className={`tabular-nums font-semibold ${b ? 'text-[#003B73] text-base font-bold' : 'text-gray-900'}`}>{c}</span>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="simple-ledger-summary">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-[11px] font-bold uppercase text-gray-500 mb-2">Ordermoms (kund)</p>
        <p className="text-2xl font-bold text-gray-900 tabular-nums">{formatSEK(orderMoms)}</p>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-1">
        <p className="text-[11px] font-bold uppercase text-gray-500 mb-1">ByggFynd intäkter</p>
        <L><Lab c="Provision inkl moms" /><Val c={formatSEK(pInkl)} /></L>
        <L><Lab c="Frakt" /><Val c={formatSEK(fr)} /></L>
        <L><Lab c="Expedition" /><Val c={formatSEK(ex)} /></L>
        <L><Lab c="BF-intäkt totalt" /><Val c={formatSEK(bfTot)} b /></L>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-[11px] font-bold uppercase text-gray-500 mb-2">ByggFynd moms</p>
        <p className="text-2xl font-bold text-gray-900 tabular-nums">{formatSEK(bfMoms)}</p>
      </div>
      <div className="rounded-lg border-2 border-[#003B73] bg-[#003B73]/5 p-4 space-y-1">
        <p className="text-[11px] font-bold uppercase text-[#003B73] mb-1">Utbetalning till butik</p>
        <L><Lab c="Varuvärde inkl moms" /><Val c={formatSEK(incl)} /></L>
        <L><Lab c="− Provision inkl moms" /><Val c={formatSEK(pInkl)} /></L>
        <div className="border-t border-[#003B73]/30 pt-2 mt-2">
          <L><Lab c="= Utbetalning" /><Val c={formatSEK(utb)} b /></L>
        </div>
      </div>
    </div>
  );
};

/** Periodens sju nyckeltal (hela marknaden / obetalt) */
const PeriodSevenTotals = ({ t }) => {
  const pIn = Number(t.total_provision_inkl_varor) || 0;
  const fr = Number(t.total_frakt_intakter) || 0;
  const ex = Number(t.total_expeditions_intakter) || 0;
  const bfTot = Number(t.total_provision_bf) || (pIn + fr + ex);
  const bfMoms = Number(t.total_moms_byggfynd_redovisning) || 0;
  const rows = [
    ['Total försäljning inkl moms', formatSEK(t.total_sales)],
    ['Total provision inkl moms', formatSEK(pIn)],
    ['Total frakt', formatSEK(fr)],
    ['Total expedition', formatSEK(ex)],
    ['Total BF-intäkt', formatSEK(bfTot)],
    ['Total BF-moms', formatSEK(bfMoms)],
    ['Total utbetalning', formatSEK(t.total_utbetalning_butiker)],
  ];
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-4 max-w-lg">
      <p className="text-xs font-bold text-gray-500 uppercase mb-3">Summering</p>
      <div className="space-y-2 text-sm">
        {rows.map(([a, b]) => (
          <div key={a} className="flex justify-between gap-4"><span className="text-gray-600">{a}</span><span className="tabular-nums font-semibold text-gray-900">{b}</span></div>
        ))}
      </div>
    </div>
  );
};

const MONTH_NAMES = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];

const getMonthRange = (monthsBack) => {
  const d = new Date();
  d.setMonth(d.getMonth() - monthsBack);
  const y = d.getFullYear();
  const m = d.getMonth();
  const first = `${y}-${String(m + 1).padStart(2, '0')}-01`;
  const last = new Date(y, m + 1, 0);
  const lastStr = `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
  return { start: first, end: lastStr, label: `${MONTH_NAMES[m]} ${y}` };
};

const getQuarterRange = (quartersBack) => {
  const d = new Date();
  const currentQ = Math.floor(d.getMonth() / 3);
  const targetQ = currentQ - quartersBack;
  const targetDate = new Date(d.getFullYear(), targetQ * 3, 1);
  if (targetQ < 0) { targetDate.setFullYear(d.getFullYear() - 1); targetDate.setMonth((targetQ + 4) * 3); }
  const y = targetDate.getFullYear();
  const qStart = Math.floor(targetDate.getMonth() / 3) * 3;
  const first = `${y}-${String(qStart + 1).padStart(2, '0')}-01`;
  const lastMonth = new Date(y, qStart + 3, 0);
  const lastStr = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}-${String(lastMonth.getDate()).padStart(2, '0')}`;
  const qNum = Math.floor(qStart / 3) + 1;
  return { start: first, end: lastStr, label: `Q${qNum} ${y}` };
};

const getYearRange = (year) => ({ start: `${year}-01-01`, end: `${year}-12-31`, label: `${year}` });

// ── Sammanfattande PDF-export (alla butiker, klientsida) ──
const exportOverviewPDF = (data) => {
  const t = data.totals || {};
  const fmt = (n) => Number(n || 0).toLocaleString('sv-SE');
  const orderMoms = Number(t.total_vat) || 0;
  const incl = Number(t.total_sales) || 0;
  const pEx = Number(t.total_provision_varor) || 0;
  const pIn = Number(t.total_provision_inkl_varor) || Math.round(pEx * 1.25 * 100) / 100;
  const fr = Number(t.total_frakt_intakter) || 0;
  const ex = Number(t.total_expeditions_intakter) || 0;
  const bfTot = Number(t.total_provision_bf) || (pIn + fr + ex);
  const bfMoms = Number(t.total_moms_byggfynd_redovisning) || 0;
  const utb = Number(t.total_utbetalning_butiker) || 0;
  const bfMomsFoot = data.stores.reduce((a, s) => a + (Number(s.moms_byggfynd) || 0), 0);
  const w = window.open('', '_blank');
  if (!w) { return; }
  w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Fakturaunderlag - Sammanfattning</title>
<style>
  @page { margin: 18mm; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a1a; margin: 0; padding: 24px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 18px; padding-bottom: 12px; border-bottom: 3px solid #003B73; }
  .logo { font-size: 28px; font-weight: 700; } .logo .bygg { color: #003B73; } .logo .fynd { color: #FF8A00; }
  .meta { text-align: right; font-size: 13px; color: #555; }
  .meta strong { display: block; font-size: 16px; color: #1a1a1a; margin-bottom: 2px; }
  .sum-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px; font-size: 10px; }
  .box { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 12px; background: #fafafa; }
  .box h4 { margin: 0 0 6px; font-size: 9px; text-transform: uppercase; color: #64748b; }
  .big { font-size: 18px; font-weight: 700; }
  .lr { display: flex; justify-content: space-between; gap: 8px; padding: 3px 0; border-bottom: 1px dotted #e5e7eb; }
  .lr:last-of-type { border-bottom: none; font-weight: 700; }
  .period-sum { margin: 16px 0; padding: 12px; background: #f8fafc; border-radius: 8px; font-size: 10px; }
  .period-sum .row { display: flex; justify-content: space-between; padding: 4px 0; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  th { background: #f0f4f8; padding: 6px 4px; text-align: left; font-weight: 600; color: #003B73; border-bottom: 2px solid #003B73; }
  td { padding: 5px 4px; border-bottom: 1px solid #e5e7eb; }
  tr:nth-child(even) td { background: #fafbfc; }
  .text-right { text-align: right; }
  tfoot td { font-weight: 700; background: #f0f4f8 !important; border-top: 2px solid #003B73; }
  .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #ddd; font-size: 10px; color: #999; text-align: center; }
  @media print { body { padding: 0; } }
</style></head><body>
<div class="header">
  <div><div class="logo"><span class="bygg">Bygg</span><span class="fynd">Fynd</span></div><div style="font-size:12px;color:#777;margin-top:4px">Fakturaunderlag – förenklad vy</div></div>
  <div class="meta"><strong>Period</strong>${data.start_date} – ${data.end_date}<br/>Sålda rader: ${t.total_products_sold ?? 0}</div>
</div>
<div class="sum-grid">
  <div class="box"><h4>Ordermoms (kund)</h4><div class="big">${fmt(orderMoms)} kr</div></div>
  <div class="box"><h4>ByggFynd moms</h4><div class="big">${fmt(bfMoms)} kr</div></div>
  <div class="box" style="grid-column:1/-1">
    <h4>ByggFynd intäkter</h4>
    <div class="lr"><span>Provision inkl moms</span><span>${fmt(pIn)} kr</span></div>
    <div class="lr"><span>Frakt</span><span>${fmt(fr)} kr</span></div>
    <div class="lr"><span>Expedition</span><span>${fmt(ex)} kr</span></div>
    <div class="lr"><span>BF-intäkt totalt</span><span>${fmt(bfTot)} kr</span></div>
  </div>
  <div class="box" style="grid-column:1/-1;border-color:#003B73;background:#f0f6fb">
    <h4>Utbetalning till butiker</h4>
    <div class="lr"><span>Varuvärde inkl moms</span><span>${fmt(incl)} kr</span></div>
    <div class="lr"><span>− Provision inkl moms</span><span>${fmt(pIn)} kr</span></div>
    <div class="lr"><span>= Utbetalning</span><span>${fmt(utb)} kr</span></div>
  </div>
</div>
<div class="period-sum">
  <strong style="display:block;margin-bottom:8px;color:#334155">Summering period</strong>
  <div class="row"><span>Total försäljning inkl moms</span><span>${fmt(incl)} kr</span></div>
  <div class="row"><span>Total provision inkl moms</span><span>${fmt(pIn)} kr</span></div>
  <div class="row"><span>Total frakt</span><span>${fmt(fr)} kr</span></div>
  <div class="row"><span>Total expedition</span><span>${fmt(ex)} kr</span></div>
  <div class="row"><span>Total BF-intäkt</span><span>${fmt(bfTot)} kr</span></div>
  <div class="row"><span>Total BF-moms</span><span>${fmt(bfMoms)} kr</span></div>
  <div class="row"><span>Total utbetalning</span><span>${fmt(utb)} kr</span></div>
</div>
<table><thead><tr><th>Byggvaruhus</th><th class="text-right">Antal</th><th class="text-right">Förs. inkl</th><th class="text-right">Pr inkl</th><th class="text-right">Frakt</th><th class="text-right">Exp.</th><th class="text-right">BF-int</th><th class="text-right">BF-moms</th><th class="text-right">Utbet.</th></tr></thead><tbody>
${data.stores.map((s) => {
    const sBf = Number(s.total_provision_bf) || ((Number(s.provision_inkl_varor) || 0) + (Number(s.frakt_intakter) || 0) + (Number(s.expeditions_intakter) || 0));
    return `<tr><td>${s.store_name}</td><td class="text-right">${s.products_sold}</td><td class="text-right">${fmt(s.total_sales)}</td><td class="text-right">${fmt(s.provision_inkl_varor)}</td><td class="text-right">${fmt(s.frakt_intakter)}</td><td class="text-right">${fmt(s.expeditions_intakter)}</td><td class="text-right">${fmt(sBf)}</td><td class="text-right">${fmt(s.moms_byggfynd)}</td><td class="text-right">${fmt(s.utbetalning_till_butik)}</td></tr>`;
  }).join('')}
</tbody><tfoot><tr><td>Totalt</td><td class="text-right">${t.total_products_sold ?? 0}</td><td class="text-right">${fmt(t.total_sales)}</td><td class="text-right">${fmt(t.total_provision_inkl_varor)}</td><td class="text-right">${fmt(t.total_frakt_intakter)}</td><td class="text-right">${fmt(t.total_expeditions_intakter)}</td><td class="text-right">${fmt(t.total_provision_bf)}</td><td class="text-right">${fmt(t.total_moms_byggfynd_redovisning ?? bfMomsFoot)}</td><td class="text-right">${fmt(t.total_utbetalning_butiker)}</td></tr></tfoot></table>
<div class="footer">Genererad ${new Date().toLocaleString('sv-SE')} via ByggFynd Fakturaunderlag</div>
</body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 400);
};

// ── Komponent: Detaljvy ──
const StoreDetailView = ({ storeId, startDate, endDate, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderSearch, setOrderSearch] = useState('');
  const [txOrderId, setTxOrderId] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await adminApi.getInvoiceStoreDetail(storeId, startDate, endDate);
        setDetail(res.data);
      } catch { toast.error('Kunde inte hämta butiksdetaljer'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [storeId, startDate, endDate]);

  const downloadCsv = async () => {
    try {
      await adminApi.downloadInvoiceUtbetalningCsv(storeId, startDate, endDate);
      toast.success('Filen sparas i din nedladdningsmapp (t.ex. Nedladdningar).');
    } catch (e) {
      toast.error(e?.message || 'Kunde inte ladda ner CSV');
    }
  };

  const downloadPdf = async () => {
    try {
      await adminApi.downloadInvoiceUtbetalningPdf(storeId, startDate, endDate);
      toast.success('Filen sparas i din nedladdningsmapp (t.ex. Nedladdningar).');
    } catch (e) {
      toast.error(e?.message || 'Kunde inte ladda ner PDF');
    }
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin text-[#003B73] mx-auto" /></div>;
  if (!detail) return null;

  const lines = detail.detail_lines?.length ? detail.detail_lines : [];
  const rate = detail.commission_rate_goods ?? detail.commission_rate ?? 0.2;
  const orderRowsAll = aggregateDetailLinesByOrder(lines);
  const orderBuyers = detail.order_buyers || {};
  const orderRows = filterInvoiceOrderRows(orderRowsAll, orderSearch, orderBuyers);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100" data-testid="invoice-store-detail">
      <div className="p-6 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition" data-testid="invoice-detail-back-btn">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-gray-900">{detail.store_name}</h2>
            <p className="text-sm text-gray-500">{detail.start_date} – {detail.end_date}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="invoice-export-csv-btn">
            <Download className="h-4 w-4 mr-1.5" />Utbetalning CSV
          </Button>
          <Button size="sm" onClick={downloadPdf} className="bg-[#003B73] hover:bg-[#002a54]" data-testid="invoice-export-pdf-btn">
            <FileText className="h-4 w-4 mr-1.5" />Utbetalning PDF
          </Button>
        </div>
      </div>

      <div className="p-6 border-b border-gray-100 bg-white space-y-4">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
          <span><span className="text-gray-400">Sålda rader:</span> <strong className="text-gray-900">{detail.products_sold}</strong></span>
          <span><span className="text-gray-400">Ort:</span> <strong className="text-gray-900">{detail.store_location || '–'}</strong></span>
        </div>
        <SimpleLedgerSummary ctx={{ ...detail, commission_rate_goods: rate }} />
      </div>

      <div className="px-6 pt-4 pb-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-sm font-bold text-gray-800">Per order</h3>
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <Input
            type="search"
            placeholder="Sök ordernr, köpare, e-post, datum, produkt, belopp …"
            value={orderSearch}
            onChange={(e) => setOrderSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
            aria-label="Sök bland ordrar"
          />
        </div>
      </div>
      {orderSearch ? (
        <p className="px-6 text-xs text-gray-500 -mt-1 mb-1">{orderRows.length} matchande order(r)</p>
      ) : null}
      <div className="overflow-x-auto px-6 pb-6">
        <table className="w-full text-xs sm:text-sm min-w-[720px]" data-testid="invoice-detail-table">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              {['Ordernr', 'Datum', 'Köpare', 'Säljare', 'Varuvärde inkl', 'Provision inkl', 'Frakt', 'Expedition', 'Utbetalning'].map((h) => (
                <th key={h} className={`p-2.5 font-semibold text-gray-600 whitespace-nowrap ${['Ordernr', 'Datum', 'Köpare', 'Säljare'].includes(h) ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr><td colSpan="9" className="p-8 text-center text-gray-400">Inga orderrader i detta intervall</td></tr>
            ) : orderRows.length === 0 ? (
              <tr><td colSpan="9" className="p-8 text-center text-gray-400">Inga ordrar matchar sökningen</td></tr>
            ) : orderRows.map((orow, i) => (
              <tr key={`${orow.order_label}-${i}`} className="border-b border-gray-100 hover:bg-blue-50/30 transition">
                <td className="p-2.5 whitespace-nowrap">
                  <button
                    type="button"
                    className="font-mono text-[#003B73] hover:underline text-left"
                    onClick={() => {
                      if (!orow.order_id) {
                        toast.error('Saknar order-id för denna rad');
                        return;
                      }
                      setTxOrderId(orow.order_id);
                    }}
                  >
                    {orow.order_label}
                  </button>
                </td>
                <td className="p-2.5 text-gray-600 whitespace-nowrap">{orow.date || '–'}</td>
                <td className="p-2.5 max-w-[160px]">
                  <div className="truncate font-medium text-gray-900" title={orderBuyers[orow.order_id]?.buyer_name}>
                    {orderBuyers[orow.order_id]?.buyer_name || '–'}
                  </div>
                  {orderBuyers[orow.order_id]?.buyer_email ? (
                    <div className="truncate text-[11px] text-gray-500" title={orderBuyers[orow.order_id].buyer_email}>
                      {orderBuyers[orow.order_id].buyer_email}
                    </div>
                  ) : null}
                </td>
                <td className="p-2.5 max-w-[140px] truncate text-gray-700" title={detail.store_name}>{detail.store_name}</td>
                <td className="p-2.5 text-right font-medium whitespace-nowrap">{formatSEK(orow.line_incl)}</td>
                <td className="p-2.5 text-right text-[#003B73] whitespace-nowrap">{formatSEK(orow.provision_inkl_varor)}</td>
                <td className="p-2.5 text-right text-gray-600 whitespace-nowrap">{formatSEK(orow.frakt)}</td>
                <td className="p-2.5 text-right text-gray-600 whitespace-nowrap">{formatSEK(orow.expedition)}</td>
                <td className="p-2.5 text-right font-semibold whitespace-nowrap">{formatSEK(orow.utbetalning_rad)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <OrderTransactionDialog
        orderId={txOrderId}
        open={Boolean(txOrderId)}
        onOpenChange={(v) => { if (!v) setTxOrderId(null); }}
      />
    </div>
  );
};

// ── Utbetalningskö: detalj (endast obetalda) ──
const PayoutStoreDetailView = ({ storeId, startDate, endDate, onClose, onMarkedPaid }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [txOrderId, setTxOrderId] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await adminApi.getPayoutQueueDetail(storeId, startDate, endDate);
        setDetail(res.data);
      } catch { toast.error('Kunde inte hämta utbetalningsdetaljer'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [storeId, startDate, endDate]);

  const downloadCsv = async () => {
    try {
      await adminApi.downloadPayoutUtbetalningCsv(storeId, startDate, endDate);
      toast.success('CSV nedladdad (obetalda rader).');
    } catch (e) { toast.error(e?.message || 'Kunde inte ladda ner CSV'); }
  };

  const downloadPdf = async () => {
    try {
      await adminApi.downloadPayoutUtbetalningPdf(storeId, startDate, endDate);
      toast.success('PDF nedladdad.');
    } catch (e) { toast.error(e?.message || 'Kunde inte ladda ner PDF'); }
  };

  const markPaid = async () => {
    if (!window.confirm('Markera alla obetalda ordrar för denna butik i perioden som utbetalda? De försvinner från utbetalningskön.')) return;
    setMarking(true);
    try {
      await adminApi.markPayoutPaid(storeId, startDate, endDate);
      toast.success('Markerat som utbetalt.');
      onMarkedPaid?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Kunde inte markera');
    } finally { setMarking(false); }
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin text-[#003B73] mx-auto" /></div>;
  if (!detail) return null;

  const lines = detail.detail_lines || [];
  const rate = detail.commission_rate_goods ?? detail.commission_rate ?? 0.2;
  const orderRowsAll = aggregateDetailLinesByOrder(lines);
  const payoutOrderBuyers = detail.order_buyers || {};
  const orderRows = filterInvoiceOrderRows(orderRowsAll, orderSearch, payoutOrderBuyers);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100" data-testid="payout-store-detail">
      <div className="p-6 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Att betala ut — {detail.store_name}</h2>
            <p className="text-sm text-gray-500">{detail.start_date} – {detail.end_date} · endast obetalda ordrar</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={downloadCsv}><Download className="h-4 w-4 mr-1.5" />CSV</Button>
          <Button size="sm" className="bg-[#003B73] hover:bg-[#002a54]" onClick={downloadPdf}><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
          <Button size="sm" variant="secondary" onClick={markPaid} disabled={marking || lines.length === 0}>
            {marking ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
            Markera utbetald
          </Button>
        </div>
      </div>

      <div className="p-6 border-b bg-white space-y-4">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
          <span><span className="text-gray-400">Obetalda ordrar:</span> <strong className="text-gray-900">{detail.order_count ?? 0}</strong></span>
        </div>
        <SimpleLedgerSummary ctx={{ ...detail, commission_rate_goods: rate }} />
      </div>

      <div className="px-6 pt-4 pb-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-sm font-bold text-gray-800">Per order</h3>
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <Input
            type="search"
            placeholder="Sök ordernr, köpare, e-post, datum, produkt, belopp …"
            value={orderSearch}
            onChange={(e) => setOrderSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
            aria-label="Sök bland ordrar"
          />
        </div>
      </div>
      {orderSearch ? (
        <p className="px-6 text-xs text-gray-500 -mt-1 mb-1">{orderRows.length} matchande order(r)</p>
      ) : null}
      <div className="overflow-x-auto px-6 pb-6">
        <table className="w-full text-xs sm:text-sm min-w-[720px]">
          <thead>
            <tr className="border-b bg-gray-50">
              {['Ordernr', 'Datum', 'Köpare', 'Säljare', 'Varuvärde inkl', 'Provision inkl', 'Frakt', 'Expedition', 'Utbetalning'].map((h) => (
                <th key={h} className={`p-2.5 font-semibold text-gray-600 whitespace-nowrap ${['Ordernr', 'Datum', 'Köpare', 'Säljare'].includes(h) ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr><td colSpan="9" className="p-8 text-center text-gray-400">Inga obetalda ordrar</td></tr>
            ) : orderRows.length === 0 ? (
              <tr><td colSpan="9" className="p-8 text-center text-gray-400">Inga ordrar matchar sökningen</td></tr>
            ) : orderRows.map((orow, i) => (
              <tr key={`${orow.order_label}-${i}`} className="border-b border-gray-100">
                <td className="p-2.5">
                  <button
                    type="button"
                    className="font-mono text-[#003B73] hover:underline text-left"
                    onClick={() => {
                      if (!orow.order_id) {
                        toast.error('Saknar order-id för denna rad');
                        return;
                      }
                      setTxOrderId(orow.order_id);
                    }}
                  >
                    {orow.order_label}
                  </button>
                </td>
                <td className="p-2.5 text-gray-600 whitespace-nowrap">{orow.date || '–'}</td>
                <td className="p-2.5 max-w-[160px]">
                  <div className="truncate font-medium text-gray-900" title={payoutOrderBuyers[orow.order_id]?.buyer_name}>
                    {payoutOrderBuyers[orow.order_id]?.buyer_name || '–'}
                  </div>
                  {payoutOrderBuyers[orow.order_id]?.buyer_email ? (
                    <div className="truncate text-[11px] text-gray-500" title={payoutOrderBuyers[orow.order_id].buyer_email}>
                      {payoutOrderBuyers[orow.order_id].buyer_email}
                    </div>
                  ) : null}
                </td>
                <td className="p-2.5 max-w-[140px] truncate text-gray-700" title={detail.store_name}>{detail.store_name}</td>
                <td className="p-2.5 text-right whitespace-nowrap">{formatSEK(orow.line_incl)}</td>
                <td className="p-2.5 text-right whitespace-nowrap text-[#003B73]">{formatSEK(orow.provision_inkl_varor)}</td>
                <td className="p-2.5 text-right whitespace-nowrap">{formatSEK(orow.frakt)}</td>
                <td className="p-2.5 text-right whitespace-nowrap">{formatSEK(orow.expedition)}</td>
                <td className="p-2.5 text-right font-semibold whitespace-nowrap">{formatSEK(orow.utbetalning_rad)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <OrderTransactionDialog
        orderId={txOrderId}
        open={Boolean(txOrderId)}
        onOpenChange={(v) => { if (!v) setTxOrderId(null); }}
      />
    </div>
  );
};

// ── Konsoliderad PDF (alla butiker med detaljer) ──
const exportAllStoresPDF = async (data, startDate, endDate, fetchStoreDetail) => {
  const t = data.totals || {};
  const fmt = (n) => Number(n || 0).toLocaleString('sv-SE');
  const w = window.open('', '_blank');
  if (!w) { toast.error('Popup blockerad – tillåt popups för att ladda ner PDF'); return; }
  w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Laddar rapport...</title>
    <style>body{font-family:'Segoe UI',Arial,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;color:#003B73;}</style>
    </head><body><div style="text-align:center"><div style="font-size:24px;font-weight:700;margin-bottom:8px">Genererar rapport...</div><div style="color:#888">Hämtar butiksdata (${data.stores.length} butiker)</div></div></body></html>`);
  w.document.close();

  const storeDetails = [];
  for (const store of data.stores) {
    try {
      const res = await fetchStoreDetail(store.store_id, startDate, endDate);
      storeDetails.push(res.data);
    } catch {
      storeDetails.push({
        store_name: store.store_name,
        detail_lines: [],
        items: [],
        total_sales: store.total_sales,
        provision_varor: store.provision_varor ?? store.commission,
        provision_inkl_varor: store.provision_inkl_varor ?? 0,
        moms_provision_varor: store.moms_provision_varor ?? 0,
        frakt_intakter: store.frakt_intakter ?? 0,
        expeditions_intakter: store.expeditions_intakter ?? 0,
        total_provision_bf: store.total_provision_bf ?? 0,
        utbetalning_till_butik: store.utbetalning_till_butik ?? 0,
        products_sold: store.products_sold,
        commission_rate_goods: data.commission_rate_goods ?? data.commission_rate ?? 0.20,
        total_ex_vat_goods: store.total_ex_vat_goods ?? 0,
        vat_goods: store.vat_goods ?? 0,
        vat_frakt: store.vat_frakt ?? 0,
        vat_expedition: store.vat_expedition ?? 0,
        moms_redovisning: store.moms_redovisning ?? 0,
        moms_byggfynd: store.moms_byggfynd ?? 0,
        store_location: '',
        store_org_number: '',
        store_email: '',
      });
    }
  }

  const ledgerBlockHtml = (x, title) => {
    const orderMoms = Number(x.moms_redovisning ?? x.total_vat) || 0;
    const incl = Number(x.total_sales) || 0;
    const pEx = Number(x.provision_varor ?? x.commission) || 0;
    const pIn = Number(x.provision_inkl_varor) || Math.round(pEx * 1.25 * 100) / 100;
    const fr = Number(x.frakt_intakter) || 0;
    const ex = Number(x.expeditions_intakter) || 0;
    const bfTot = Number(x.total_provision_bf) || (pIn + fr + ex);
    const bfMoms = Number(x.moms_byggfynd ?? x.total_moms_byggfynd_redovisning) || 0;
    const utb = Number(x.utbetalning_till_butik) || 0;
    return `
    <div style="font-size:10px;border:1px solid #e5e7eb;border-radius:8px;padding:10px;margin-bottom:10px;background:#fafafa">
      <div style="font-weight:700;color:#334155;margin-bottom:8px">${title}</div>
      <table style="width:100%;border-collapse:collapse"><tbody>
        <tr><td style="padding:4px 0">Ordermoms (kund)</td><td style="text-align:right;font-weight:700">${fmt(orderMoms)} kr</td></tr>
        <tr><td style="padding:4px 0">Provision inkl moms</td><td style="text-align:right">${fmt(pIn)} kr</td></tr>
        <tr><td style="padding:4px 0">Frakt / expedition</td><td style="text-align:right">${fmt(fr)} / ${fmt(ex)} kr</td></tr>
        <tr><td style="padding:4px 0;font-weight:700">BF-intäkt totalt</td><td style="text-align:right;font-weight:700">${fmt(bfTot)} kr</td></tr>
        <tr><td style="padding:4px 0">BF-moms</td><td style="text-align:right;font-weight:700">${fmt(bfMoms)} kr</td></tr>
        <tr><td style="padding:4px 0;font-weight:700;color:#003B73">Utbetalning</td><td style="text-align:right;font-weight:700;color:#003B73">${fmt(utb)} kr</td></tr>
        <tr><td colspan="2" style="font-size:9px;color:#64748b;padding-top:6px">Varuvärde inkl ${fmt(incl)} kr − provision inkl ${fmt(pIn)} kr</td></tr>
      </tbody></table>
    </div>`;
  };

  const rowHtml = (sd) => {
    const L = sd.detail_lines?.length ? sd.detail_lines : (sd.items || []);
    if (!L.length) return '<p style="color:#999;text-align:center;padding:24px">Inga orderrader</p>';
    const rows = aggregateDetailLinesByOrder(L);
    return `<table><thead><tr><th>Order-ID</th><th class="text-right">Varuvärde inkl</th><th class="text-right">Provision inkl</th><th class="text-right">Frakt</th><th class="text-right">Expedition</th><th class="text-right">Utbetalning</th></tr></thead><tbody>
    ${rows.map((orow) => `<tr><td>${orow.order_label}</td><td class="text-right">${fmt(orow.line_incl)}</td><td class="text-right">${fmt(orow.provision_inkl_varor)}</td><td class="text-right">${fmt(orow.frakt)}</td><td class="text-right">${fmt(orow.expedition)}</td><td class="text-right">${fmt(orow.utbetalning_rad)}</td></tr>`).join('')}
    </tbody></table>`;
  };

  const orderMoms0 = Number(t.total_vat) || 0;
  const incl0 = Number(t.total_sales) || 0;
  const pEx0 = Number(t.total_provision_varor) || 0;
  const pIn0 = Number(t.total_provision_inkl_varor) || Math.round(pEx0 * 1.25 * 100) / 100;
  const fr0 = Number(t.total_frakt_intakter) || 0;
  const ex0 = Number(t.total_expeditions_intakter) || 0;
  const bfTot0 = Number(t.total_provision_bf) || (pIn0 + fr0 + ex0);
  const bfMoms0 = Number(t.total_moms_byggfynd_redovisning) || 0;
  const utb0 = Number(t.total_utbetalning_butiker) || 0;
  const bfMomsFootAll = data.stores.reduce((a, s) => a + (Number(s.moms_byggfynd) || 0), 0);

  const storePages = storeDetails.map((sd) => `
    <div class="page-break">
      <h2 style="color:#003B73;border-bottom:2px solid #003B73;padding-bottom:8px;margin-top:0">${sd.store_name}</h2>
      <div class="info-row">
        <span><strong>Ort:</strong> ${sd.store_location || '–'}</span>
        <span><strong>Org.nr:</strong> ${sd.store_org_number || '–'}</span>
        <span><strong>E-post:</strong> ${sd.store_email || '–'}</span>
        <span><strong>Sålda rader:</strong> ${sd.products_sold}</span>
      </div>
      ${ledgerBlockHtml(sd, 'Summering')}
      <p style="font-size:10px;color:#555;margin:8px 0 4px">Per order</p>
      ${rowHtml(sd)}
    </div>`).join('');

  w.document.open();
  w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Konsoliderad rapport – ByggFynd</title>
<style>
  @page { margin: 16mm; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a1a; margin: 0; padding: 24px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; padding-bottom: 12px; border-bottom: 3px solid #003B73; }
  .logo { font-size: 28px; font-weight: 700; } .logo .bygg { color: #003B73; } .logo .fynd { color: #FF8A00; }
  .meta { text-align: right; font-size: 13px; color: #555; }
  .meta strong { display: block; font-size: 16px; color: #1a1a1a; margin-bottom: 2px; }
  .summary-cards { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 12px; margin-bottom: 24px; }
  .card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 14px; }
  .card .label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.05em; }
  .card .value { font-size: 18px; font-weight: 700; margin-top: 2px; }
  .card .value.blue { color: #003B73; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 8px; }
  th { background: #f0f4f8; padding: 6px 8px; text-align: left; font-weight: 600; color: #003B73; border-bottom: 2px solid #003B73; }
  td { padding: 5px 8px; border-bottom: 1px solid #e5e7eb; }
  tr:nth-child(even) td { background: #fafbfc; }
  .text-right { text-align: right; }
  tfoot td { font-weight: 700; background: #f0f4f8 !important; border-top: 2px solid #003B73; }
  .page-break { page-break-before: always; padding-top: 16px; }
  .page-break:first-child { page-break-before: avoid; }
  .info-row { display: flex; gap: 24px; font-size: 12px; color: #555; margin-bottom: 12px; flex-wrap: wrap; }
  .overview-table { margin-bottom: 0; font-size: 11px; }
  h2 { font-size: 16px; margin: 0 0 12px; }
  .footer { margin-top: 36px; padding-top: 12px; border-top: 1px solid #ddd; font-size: 11px; color: #999; text-align: center; }
  .sum-top { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 18px; font-size: 10px; }
  .sum-box { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; background: #fafafa; }
  .sum-box h4 { margin: 0 0 6px; font-size: 9px; text-transform: uppercase; color: #64748b; }
  .lr2 { display: flex; justify-content: space-between; gap: 8px; padding: 3px 0; border-bottom: 1px dotted #e5e7eb; font-size: 10px; }
  .lr2:last-child { border-bottom: none; font-weight: 700; }
  @media print { body { padding: 0; } .page-break { page-break-before: always; } }
</style></head><body>
<div class="header">
  <div><div class="logo"><span class="bygg">Bygg</span><span class="fynd">Fynd</span></div><div style="font-size:12px;color:#777;margin-top:4px">Konsoliderad rapport – alla butiker</div></div>
  <div class="meta"><strong>Fakturaunderlag</strong>${startDate} – ${endDate}<br/>${data.stores.length} butiker · ${t.total_products_sold ?? 0} sålda rader</div>
</div>
<div class="sum-top">
  <div class="sum-box"><h4>Ordermoms (kund)</h4><div style="font-size:18px;font-weight:700">${fmt(orderMoms0)} kr</div></div>
  <div class="sum-box"><h4>BF-moms</h4><div style="font-size:18px;font-weight:700">${fmt(bfMoms0)} kr</div></div>
  <div class="sum-box" style="grid-column:1/-1">
    <h4>ByggFynd intäkter</h4>
    <div class="lr2"><span>Provision inkl moms</span><span>${fmt(pIn0)} kr</span></div>
    <div class="lr2"><span>Frakt</span><span>${fmt(fr0)} kr</span></div>
    <div class="lr2"><span>Expedition</span><span>${fmt(ex0)} kr</span></div>
    <div class="lr2"><span>BF-intäkt totalt</span><span>${fmt(bfTot0)} kr</span></div>
  </div>
  <div class="sum-box" style="grid-column:1/-1;border-color:#003B73;background:#f0f6fb">
    <h4>Utbetalning till butiker</h4>
    <div class="lr2"><span>Varuvärde inkl moms</span><span>${fmt(incl0)} kr</span></div>
    <div class="lr2"><span>− Provision inkl moms</span><span>${fmt(pIn0)} kr</span></div>
    <div class="lr2"><span>= Utbetalning</span><span>${fmt(utb0)} kr</span></div>
  </div>
</div>
<h2 style="color:#003B73">Översikt per butik</h2>
<table class="overview-table"><thead><tr><th>Byggvaruhus</th><th class="text-right">Antal</th><th class="text-right">Förs. inkl</th><th class="text-right">Pr inkl</th><th class="text-right">Frakt</th><th class="text-right">Exp.</th><th class="text-right">BF-int</th><th class="text-right">BF-moms</th><th class="text-right">Utbet.</th></tr></thead><tbody>
${data.stores.map((s) => {
    const sBf = Number(s.total_provision_bf) || ((Number(s.provision_inkl_varor) || 0) + (Number(s.frakt_intakter) || 0) + (Number(s.expeditions_intakter) || 0));
    return `<tr><td>${s.store_name}</td><td class="text-right">${s.products_sold}</td><td class="text-right">${fmt(s.total_sales)}</td><td class="text-right">${fmt(s.provision_inkl_varor)}</td><td class="text-right">${fmt(s.frakt_intakter)}</td><td class="text-right">${fmt(s.expeditions_intakter)}</td><td class="text-right">${fmt(sBf)}</td><td class="text-right">${fmt(s.moms_byggfynd)}</td><td class="text-right">${fmt(s.utbetalning_till_butik)}</td></tr>`;
  }).join('')}
</tbody><tfoot><tr><td>Totalt</td><td class="text-right">${t.total_products_sold ?? 0}</td><td class="text-right">${fmt(t.total_sales)}</td><td class="text-right">${fmt(t.total_provision_inkl_varor)}</td><td class="text-right">${fmt(t.total_frakt_intakter)}</td><td class="text-right">${fmt(t.total_expeditions_intakter)}</td><td class="text-right">${fmt(t.total_provision_bf)}</td><td class="text-right">${fmt(t.total_moms_byggfynd_redovisning ?? bfMomsFootAll)}</td><td class="text-right">${fmt(t.total_utbetalning_butiker)}</td></tr></tfoot></table>
${storePages}
<div class="footer">Genererad ${new Date().toLocaleString('sv-SE')} via ByggFynd Fakturaunderlag</div>
</body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 500);
};

// ── Huvudsida ──
const InvoiceDataPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, isAuthenticated, loading: authLoading } = useAuth();

  // Default: innevarande månad
  const now = new Date();
  const firstDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const lastDayStr = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;

  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDayStr);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [payoutDetailStoreId, setPayoutDetailStoreId] = useState(null);
  const [invoiceTab, setInvoiceTab] = useState(() =>
    (searchParams.get('vy') === 'utbetalning' ? 'attbetala' : 'helhet'),
  );
  const [payoutData, setPayoutData] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [activePreset, setActivePreset] = useState('current');
  const [exportingAll, setExportingAll] = useState(false);
  const [periodOrders, setPeriodOrders] = useState([]);
  const [periodOrderSearch, setPeriodOrderSearch] = useState('');
  const [globalOrderSearch, setGlobalOrderSearch] = useState('');
  const [globalOrderResults, setGlobalOrderResults] = useState([]);
  const [globalOrderSearchLoading, setGlobalOrderSearchLoading] = useState(false);
  const [invoiceTxOrderId, setInvoiceTxOrderId] = useState(null);

  const presets = [
    { id: 'current', label: 'Denna månad', ...getMonthRange(0) },
    { id: 'prev', label: 'Förra månaden', ...getMonthRange(1) },
    { id: 'prev2', label: getMonthRange(2).label, ...getMonthRange(2) },
    { id: 'q-current', label: getQuarterRange(0).label, ...getQuarterRange(0) },
    { id: 'q-prev', label: getQuarterRange(1).label, ...getQuarterRange(1) },
    { id: 'year', label: `Hela ${new Date().getFullYear()}`, ...getYearRange(new Date().getFullYear()) },
  ];

  const applyPreset = (preset) => {
    setActivePreset(preset.id);
    setStartDate(preset.start);
    setEndDate(preset.end);
  };

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) navigate('/logga-in');
  }, [authLoading, isAuthenticated, isAdmin, navigate]);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const res = await adminApi.getInvoiceData(startDate, endDate);
      setData(res.data);
      try {
        const pres = await adminApi.getInvoicePeriodOrders(startDate, endDate);
        setPeriodOrders(pres.data?.orders || []);
      } catch {
        setPeriodOrders([]);
      }
    } catch {
      toast.error('Kunde inte hämta fakturaunderlag');
      setData(null);
      setPeriodOrders([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const runGlobalOrderSearch = async () => {
    const q = globalOrderSearch.trim();
    if (q.length < 2) {
      toast.error('Skriv minst 2 tecken');
      return;
    }
    setGlobalOrderSearchLoading(true);
    try {
      const res = await adminApi.searchInvoiceOrders(q);
      const hits = res.data?.results || [];
      setGlobalOrderResults(hits);
      if (hits.length === 0) {
        toast.info('Inga ordrar hittades');
      }
    } catch {
      toast.error('Kunde inte söka bland ordrar');
      setGlobalOrderResults([]);
    } finally {
      setGlobalOrderSearchLoading(false);
    }
  };

  const fetchPayoutData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setPayoutLoading(true);
    try {
      const res = await adminApi.getPayoutQueue(startDate, endDate);
      setPayoutData(res.data);
    } catch {
      toast.error('Kunde inte hämta utbetalningskö');
      setPayoutData(null);
    } finally {
      setPayoutLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { if (isAdmin) fetchData(); }, [isAdmin, fetchData]);

  useEffect(() => {
    if (searchParams.get('vy') === 'utbetalning') {
      setInvoiceTab('attbetala');
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isAdmin || invoiceTab !== 'attbetala') return;
    fetchPayoutData();
  }, [isAdmin, invoiceTab, fetchPayoutData]);

  const goHelhetTab = () => {
    setInvoiceTab('helhet');
    setPayoutDetailStoreId(null);
    setSearchParams({}, { replace: true });
  };

  const goUtbetalningTab = () => {
    setInvoiceTab('attbetala');
    setSelectedStoreId(null);
    setSearchParams({ vy: 'utbetalning' }, { replace: true });
  };

  const handleExportAll = async () => {
    if (!data || data.stores.length === 0) return;
    setExportingAll(true);
    try {
      await exportAllStoresPDF(data, startDate, endDate, adminApi.getInvoiceStoreDetail);
    } catch { toast.error('Kunde inte generera konsoliderad rapport'); }
    finally { setExportingAll(false); }
  };

  const runServerExport = async (fn, okMsg) => {
    try {
      await fn(startDate, endDate);
      if (okMsg) {
        toast.success(`${okMsg} Filen hamnar i din nedladdningsmapp.`);
      }
    } catch (e) {
      toast.error(e?.message || 'Export misslyckades');
    }
  };

  if (authLoading) return <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin text-[#003B73] mx-auto" /></div>;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="invoice-data-page">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 rounded-lg hover:bg-gray-100 transition" data-testid="invoice-back-to-admin-btn">
              <ArrowLeft className="h-5 w-5 text-gray-500" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Fakturaunderlag</h1>
              <p className="text-sm text-gray-500">
                {invoiceTab === 'helhet'
                  ? 'Ren periodrapport — läsning och export. Ingen utbetalningsåtgärd här.'
                  : 'Arbetsyta: betala ut till butiker och markera ordrar som utbetalda.'}
              </p>
              <div className="flex gap-2 mt-3" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={invoiceTab === 'helhet'}
                  onClick={goHelhetTab}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${invoiceTab === 'helhet' ? 'bg-[#003B73] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  Fakturaunderlag
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={invoiceTab === 'attbetala'}
                  onClick={goUtbetalningTab}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${invoiceTab === 'attbetala' ? 'bg-[#003B73] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  Att betala ut
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Datumfilter */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4" data-testid="invoice-date-filter">
          {/* Snabbval */}
          <div className="flex flex-wrap gap-2" data-testid="invoice-presets">
            {presets.map((preset) => (
              <button
                key={preset.id}
                onClick={() => applyPreset(preset)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all ${
                  activePreset === preset.id
                    ? 'bg-[#003B73] text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`invoice-preset-${preset.id}`}
              >
                {preset.id === 'prev' && <Clock className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />}
                {preset.label}
              </button>
            ))}
          </div>
          {/* Manuellt datumval */}
          <div className="flex flex-wrap items-end gap-4 pt-2 border-t border-gray-100">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Startdatum</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <Input
                  type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setActivePreset(null); }}
                  className="pl-10 w-44" data-testid="invoice-start-date"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Slutdatum</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <Input
                  type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setActivePreset(null); }}
                  className="pl-10 w-44" data-testid="invoice-end-date"
                />
              </div>
            </div>
            <Button
              onClick={() => (invoiceTab === 'helhet' ? fetchData() : fetchPayoutData())}
              disabled={invoiceTab === 'helhet' ? loading : payoutLoading}
              className="bg-[#003B73] hover:bg-[#002a54]"
              data-testid="invoice-fetch-btn"
            >
              {(invoiceTab === 'helhet' ? loading : payoutLoading) ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              Hämta data
            </Button>
          </div>
        </div>

        {/* Detaljvy eller översikt */}
        {payoutDetailStoreId ? (
          <PayoutStoreDetailView
            storeId={payoutDetailStoreId}
            startDate={startDate}
            endDate={endDate}
            onClose={() => setPayoutDetailStoreId(null)}
            onMarkedPaid={fetchPayoutData}
          />
        ) : selectedStoreId ? (
          <StoreDetailView
            storeId={selectedStoreId}
            startDate={startDate}
            endDate={endDate}
            onClose={() => setSelectedStoreId(null)}
          />
        ) : invoiceTab === 'attbetala' ? (
          <>
            <div className="bg-amber-50/90 border border-amber-200 rounded-xl p-4 text-sm text-amber-950">
              <p className="text-amber-900/90">Här visas endast obetalda ordrar. Utbetalning = varuvärde inkl moms − provision inkl. När pengar skickats: <strong>Markera som utbetald</strong>.</p>
            </div>
            {payoutLoading ? (
              <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin text-[#003B73] mx-auto" /></div>
            ) : payoutData && (() => {
              const pt = payoutData.totals || {};
              const provBf = pt.total_provision_bf ?? pt.total_commission ?? 0;
              return (
              <>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 sm:p-6 space-y-5">
                  <p className="text-sm font-semibold text-gray-800">Obetalda ordrar — summering</p>
                  <SimpleLedgerSummary ctx={{
                    ...pt,
                    commission_rate_goods: payoutData.commission_rate_goods ?? payoutData.commission_rate ?? 0.2,
                  }}
                  />
                  <PeriodSevenTotals t={pt} />
                </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-5 border-b flex flex-wrap justify-between gap-2">
                  <div>
                    <h2 className="text-base font-bold text-gray-900">Utbetalningskö</h2>
                    <p className="text-sm text-gray-500">{payoutData.stores?.length ?? 0} byggvaruhus med obetalda ordrar</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => runServerExport(adminApi.downloadPayoutFortnoxCsv, 'Fortnox (obetalda)')}>
                      <Download className="h-4 w-4 mr-1.5" />Fortnox CSV
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => runServerExport(adminApi.downloadPayoutUtbetalningAllCsv, 'Samlad utbetalning (obetalda)')}>
                      <Download className="h-4 w-4 mr-1.5" />Alla CSV
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[10px] sm:text-xs min-w-[720px]">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        {['Byggvaruhus', 'Ordrar', 'Utbetalning', 'BF-intäkt', 'BF-moms', 'Detalj', 'Export', 'Utbetald'].map((h, hi) => (
                          <th key={`payout-h-${hi}`} className={`p-2 font-semibold text-gray-600 whitespace-nowrap ${hi === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(payoutData.stores || []).length === 0 ? (
                        <tr><td colSpan="8" className="p-12 text-center text-gray-400">Inget obetalt underlag i perioden</td></tr>
                      ) : (payoutData.stores || []).map((s) => (
                        <tr key={s.store_id} className="border-b border-gray-100 hover:bg-blue-50/30">
                          <td className="p-2 font-medium text-left">{s.store_name}</td>
                          <td className="p-2 text-right">{s.order_count ?? 0}</td>
                          <td className="p-2 text-right font-semibold text-[#003B73] whitespace-nowrap">{formatSEK(s.utbetalning_till_butik)}</td>
                          <td className="p-2 text-right whitespace-nowrap">{formatSEK(s.total_provision_bf)}</td>
                          <td className="p-2 text-right whitespace-nowrap">{formatSEK(s.moms_byggfynd)}</td>
                          <td className="p-2 text-right">
                            <Button type="button" size="sm" variant="ghost" className="text-[#003B73] h-7 text-[10px]" onClick={() => setPayoutDetailStoreId(s.store_id)}>
                              Visa <ChevronRight className="h-3 w-3 ml-0.5 inline" />
                            </Button>
                          </td>
                          <td className="p-2 text-right whitespace-nowrap">
                            <div className="flex flex-wrap gap-1 justify-end">
                              <Button type="button" size="sm" variant="outline" className="h-7 px-1.5 text-[10px]" onClick={() => adminApi.downloadPayoutUtbetalningPdf(s.store_id, startDate, endDate).then(() => toast.success('PDF nedladdad')).catch((e) => toast.error(e?.message))}>PDF</Button>
                              <Button type="button" size="sm" variant="outline" className="h-7 px-1.5 text-[10px]" onClick={() => adminApi.downloadPayoutUtbetalningCsv(s.store_id, startDate, endDate).then(() => toast.success('CSV nedladdad')).catch((e) => toast.error(e?.message))}>CSV</Button>
                            </div>
                          </td>
                          <td className="p-2 text-right">
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              className="h-7 text-[10px]"
                              disabled={!s.order_count}
                              onClick={async () => {
                                if (!window.confirm(`Markera alla obetalda ordrar för ${s.store_name} som utbetalda?`)) return;
                                try {
                                  await adminApi.markPayoutPaid(s.store_id, startDate, endDate);
                                  toast.success('Markerat som utbetalt.');
                                  fetchPayoutData();
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || e?.message || 'Fel');
                                }
                              }}
                            >
                              Klar
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {(payoutData.stores || []).length > 0 && (
                      <tfoot>
                        <tr className="bg-gray-50 border-t-2 border-[#003B73] font-bold">
                          <td className="p-2 text-left">Totalt</td>
                          <td className="p-2 text-right">{(payoutData.stores || []).reduce((a, s) => a + (s.order_count || 0), 0)}</td>
                          <td className="p-2 text-right whitespace-nowrap text-[#003B73]">{formatSEK(pt.total_utbetalning_butiker)}</td>
                          <td className="p-2 text-right whitespace-nowrap">{formatSEK(provBf)}</td>
                          <td className="p-2 text-right whitespace-nowrap">{formatSEK(pt.total_moms_byggfynd_redovisning)}</td>
                          <td colSpan="3" />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
              </>
              );
            })()}
          </>
        ) : (
          <>
            {/* Sammanfattning */}
            {data && (() => {
              const t = data.totals || {};
              return (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 sm:p-6 space-y-5" data-testid="invoice-summary-cards">
                  <p className="text-sm font-semibold text-gray-800">Periodens summering (alla butiker)</p>
                  <SimpleLedgerSummary ctx={{
                    ...t,
                    commission_rate_goods: t.commission_rate_goods ?? data.commission_rate ?? 0.2,
                  }}
                  />
                  <PeriodSevenTotals t={t} />
                </div>
              );
            })()}

            {data && !loading && (
              <div className="space-y-4" data-testid="invoice-period-orders-panel">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-5 border-b border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h2 className="text-base font-bold text-gray-900">Ordrar i vald period</h2>
                      <p className="text-sm text-gray-500">
                        {periodOrders.length} order(r) · filtrera och klicka på ordernr för <strong>orderbekräftelse / transaktion</strong>
                      </p>
                    </div>
                    <div className="relative w-full sm:max-w-md">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      <Input
                        type="search"
                        placeholder="Sök ordernr, köpare, säljare, datum, belopp …"
                        value={periodOrderSearch}
                        onChange={(e) => setPeriodOrderSearch(e.target.value)}
                        className="pl-9 h-9 text-sm"
                        aria-label="Sök bland ordrar i perioden"
                      />
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs sm:text-sm min-w-[720px]">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          {['Ordernr', 'Datum', 'Köpare', 'Säljare', 'Varuvärde inkl', 'Provision inkl', 'Frakt', 'Expedition', 'Utbetalning'].map((h) => (
                            <th key={h} className={`p-2.5 font-semibold text-gray-600 whitespace-nowrap ${['Ordernr', 'Datum', 'Köpare', 'Säljare'].includes(h) ? 'text-left' : 'text-right'}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const rows = filterPeriodOrderRows(periodOrders, periodOrderSearch);
                          if (periodOrders.length === 0) {
                            return <tr><td colSpan="9" className="p-8 text-center text-gray-400">Inga ordrar i vald period</td></tr>;
                          }
                          if (rows.length === 0) {
                            return <tr><td colSpan="9" className="p-8 text-center text-gray-400">Inga ordrar matchar sökningen</td></tr>;
                          }
                          return rows.map((orow, i) => (
                            <tr key={`${orow.order_id}-${i}`} className="border-b border-gray-100 hover:bg-blue-50/30 transition">
                              <td className="p-2.5 whitespace-nowrap">
                                <button
                                  type="button"
                                  className="font-mono text-[#003B73] hover:underline text-left"
                                  onClick={() => {
                                    if (!orow.order_id) {
                                      toast.error('Saknar order-id för denna rad');
                                      return;
                                    }
                                    setInvoiceTxOrderId(orow.order_id);
                                  }}
                                >
                                  {orow.order_label}
                                </button>
                              </td>
                              <td className="p-2.5 text-gray-600 whitespace-nowrap">{orow.date || '–'}</td>
                              <td className="p-2.5 max-w-[160px]">
                                <div className="truncate font-medium text-gray-900" title={orow.buyer_name}>{orow.buyer_name || '–'}</div>
                                {orow.buyer_email ? (
                                  <div className="truncate text-[11px] text-gray-500" title={orow.buyer_email}>{orow.buyer_email}</div>
                                ) : null}
                              </td>
                              <td className="p-2.5 max-w-[180px] truncate text-gray-700" title={orow.sellers_label}>{orow.sellers_label || '–'}</td>
                              <td className="p-2.5 text-right font-medium whitespace-nowrap">{formatSEK(orow.line_incl)}</td>
                              <td className="p-2.5 text-right text-[#003B73] whitespace-nowrap">{formatSEK(orow.provision_inkl_varor)}</td>
                              <td className="p-2.5 text-right text-gray-600 whitespace-nowrap">{formatSEK(orow.frakt)}</td>
                              <td className="p-2.5 text-right text-gray-600 whitespace-nowrap">{formatSEK(orow.expedition)}</td>
                              <td className="p-2.5 text-right font-semibold whitespace-nowrap">{formatSEK(orow.utbetalning_rad)}</td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-5 border-b border-gray-100 space-y-3">
                    <div>
                      <h2 className="text-base font-bold text-gray-900">Sök order i hela registret</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Hitta ordrar utanför vald period (ordernummer, order-id, kundnamn eller e-post). Öppna detalj med samma ordervy som ovan.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 max-w-2xl">
                      <Input
                        value={globalOrderSearch}
                        onChange={(e) => setGlobalOrderSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') runGlobalOrderSearch(); }}
                        placeholder="Minst 2 tecken …"
                        className="h-9"
                        aria-label="Global ordersökning"
                      />
                      <Button
                        type="button"
                        onClick={runGlobalOrderSearch}
                        disabled={globalOrderSearchLoading}
                        className="inline-flex items-center gap-1.5 bg-[#003B73] hover:bg-[#002a54] shrink-0"
                      >
                        {globalOrderSearchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                        Sök
                      </Button>
                    </div>
                  </div>
                  {globalOrderResults.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs sm:text-sm min-w-[640px]">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            {['Ordernr', 'Datum', 'Köpare', 'Belopp', 'Status', ''].map((h, hi) => (
                              <th key={hi} className={`p-2.5 font-semibold text-gray-600 whitespace-nowrap ${hi < 3 ? 'text-left' : 'text-right'}`}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {globalOrderResults.map((r) => (
                            <tr key={r.id} className="border-b border-gray-100 hover:bg-blue-50/30">
                              <td className="p-2.5 whitespace-nowrap">
                                <button
                                  type="button"
                                  className="font-mono text-[#003B73] hover:underline text-left"
                                  onClick={() => setInvoiceTxOrderId(r.id)}
                                >
                                  {r.order_number || String(r.id).slice(0, 8).toUpperCase()}
                                </button>
                              </td>
                              <td className="p-2.5 text-gray-600 whitespace-nowrap">{formatDate(r.created_at)}</td>
                              <td className="p-2.5 max-w-[200px]">
                                <div className="truncate font-medium text-gray-900" title={r.customer_name}>{r.customer_name || '–'}</div>
                                {r.customer_email ? (
                                  <div className="truncate text-[11px] text-gray-500" title={r.customer_email}>{r.customer_email}</div>
                                ) : null}
                              </td>
                              <td className="p-2.5 text-right whitespace-nowrap">{formatSEK(r.grand_total)}</td>
                              <td className="p-2.5 text-right text-gray-600 whitespace-nowrap text-[11px]">
                                {[r.payment_status, r.status].filter(Boolean).join(' · ') || '–'}
                              </td>
                              <td className="p-2.5 text-right">
                                <Button type="button" size="sm" variant="ghost" className="h-7 text-[10px] text-[#003B73]" onClick={() => setInvoiceTxOrderId(r.id)}>
                                  Visa
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {/* Butikstabell */}
            {loading ? (
              <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin text-[#003B73] mx-auto" /></div>
            ) : data && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="invoice-stores-table">
                <div className="p-5 border-b border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-base font-bold text-gray-900">Per byggvaruhus</h2>
                    <p className="text-sm text-gray-500">{data.stores.length} butiker · använd export per rad eller samlingsfiler för hela perioden.</p>
                  </div>
                  {data.stores.length > 0 && (
                    <div className="flex flex-wrap gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => runServerExport(adminApi.downloadInvoiceOverviewCsv, 'Översikt CSV nedladdad')} data-testid="invoice-overview-csv-btn">
                        <Download className="h-4 w-4 mr-1.5" />Hel period · översikt CSV
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        title="Kolumnen Konto är tom tills ni satt konton — fyll i vid import till Fortnox."
                        onClick={() => runServerExport(adminApi.downloadInvoiceFortnoxCsv, 'Fortnox CSV (hel period) nedladdad')}
                      >
                        <Download className="h-4 w-4 mr-1.5" />Hel period · Fortnox
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => runServerExport(adminApi.downloadInvoiceUtbetalningAllCsv, 'Samlad utbetalning CSV nedladdad')}>
                        <Download className="h-4 w-4 mr-1.5" />Hel period · utbetalning CSV
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => exportOverviewPDF(data)} className="border-[#003B73] text-[#003B73] hover:bg-[#003B73]/5" data-testid="invoice-overview-pdf-btn">
                        <FileText className="h-4 w-4 mr-1.5" />Hel period · översikt PDF
                      </Button>
                      <Button size="sm" onClick={handleExportAll} disabled={exportingAll} className="bg-[#003B73] hover:bg-[#002a54]" data-testid="invoice-export-all-pdf-btn">
                        {exportingAll ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileText className="h-4 w-4 mr-1.5" />}
                        Hel period · alla butiker PDF
                      </Button>
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm min-w-[880px]">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="text-left p-3 font-semibold text-gray-600">Byggvaruhus</th>
                        <th className="text-right p-3 font-semibold text-gray-600 whitespace-nowrap">Försäljning inkl</th>
                        <th className="text-right p-3 font-semibold text-gray-600 whitespace-nowrap">Provision inkl</th>
                        <th className="text-right p-3 font-semibold text-gray-600 whitespace-nowrap">Frakt</th>
                        <th className="text-right p-3 font-semibold text-gray-600 whitespace-nowrap">Expedition</th>
                        <th className="text-right p-3 font-semibold text-gray-600 whitespace-nowrap">BF-intäkt</th>
                        <th className="text-right p-3 font-semibold text-gray-600 whitespace-nowrap">BF-moms</th>
                        <th className="text-right p-3 font-semibold text-gray-600 whitespace-nowrap">Utbetalning</th>
                        <th className="text-right p-3 font-semibold text-gray-600 whitespace-nowrap">Export</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.stores.length === 0 ? (
                        <tr><td colSpan="9" className="p-12 text-center text-gray-400">Ingen försäljning under valt datumintervall</td></tr>
                      ) : data.stores.map((s) => {
                        const sBf = Number(s.total_provision_bf) || ((Number(s.provision_inkl_varor) || 0) + (Number(s.frakt_intakter) || 0) + (Number(s.expeditions_intakter) || 0));
                        return (
                        <tr key={s.store_id} className="border-b border-gray-100 hover:bg-blue-50/30 transition" data-testid={`invoice-store-row-${s.store_id}`}>
                          <td className="p-3">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg bg-[#003B73]/10 flex items-center justify-center flex-shrink-0">
                                <Store className="h-4 w-4 text-[#003B73]" />
                              </div>
                              <span className="font-medium text-gray-900">{s.store_name}</span>
                            </div>
                          </td>
                          <td className="p-3 text-right font-semibold text-gray-900 whitespace-nowrap">{formatSEK(s.total_sales)}</td>
                          <td className="p-3 text-right font-semibold text-[#003B73] whitespace-nowrap">{formatSEK(s.provision_inkl_varor ?? 0)}</td>
                          <td className="p-3 text-right text-gray-700 whitespace-nowrap">{formatSEK(s.frakt_intakter)}</td>
                          <td className="p-3 text-right text-gray-700 whitespace-nowrap">{formatSEK(s.expeditions_intakter)}</td>
                          <td className="p-3 text-right text-gray-800 whitespace-nowrap">{formatSEK(sBf)}</td>
                          <td className="p-3 text-right text-gray-600 whitespace-nowrap">{formatSEK(s.moms_byggfynd ?? 0)}</td>
                          <td className="p-3 text-right font-semibold text-[#003B73] whitespace-nowrap">{formatSEK(s.utbetalning_till_butik)}</td>
                          <td className="p-3 text-right whitespace-nowrap">
                            <div className="flex flex-wrap gap-1 justify-end items-center">
                              <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-[10px]" onClick={() => adminApi.downloadInvoiceUtbetalningPdf(s.store_id, startDate, endDate).then(() => toast.success('PDF nedladdad')).catch((e) => toast.error(e?.message))}>PDF</Button>
                              <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-[10px]" onClick={() => adminApi.downloadInvoiceUtbetalningCsv(s.store_id, startDate, endDate).then(() => toast.success('CSV nedladdad')).catch((e) => toast.error(e?.message))}>CSV</Button>
                              <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-[10px]" title="Fortnox-rader för denna butik" onClick={() => adminApi.downloadInvoiceFortnoxCsvForStore(s.store_id, startDate, endDate).then(() => toast.success('Fortnox-fil nedladdad')).catch((e) => toast.error(e?.message))}>FN</Button>
                              <Button type="button" size="sm" variant="ghost" className="h-7 px-1.5 text-[10px] text-[#003B73]" onClick={() => setSelectedStoreId(s.store_id)}>Ordrar</Button>
                            </div>
                          </td>
                        </tr>
                      ); })}
                    </tbody>
                    {data.stores.length > 0 && (() => {
                      const t = data.totals || {};
                      const bfMomsSum = data.stores.reduce((a, s) => a + (Number(s.moms_byggfynd) || 0), 0);
                      return (
                        <tfoot>
                          <tr className="bg-gray-50 border-t-2 border-[#003B73]">
                            <td className="p-3 font-bold text-gray-900">Totalt</td>
                            <td className="p-3 text-right font-bold text-gray-900 whitespace-nowrap">{formatSEK(t.total_sales)}</td>
                            <td className="p-3 text-right font-bold text-[#003B73] whitespace-nowrap">{formatSEK(t.total_provision_inkl_varor ?? 0)}</td>
                            <td className="p-3 text-right font-bold text-gray-900 whitespace-nowrap">{formatSEK(t.total_frakt_intakter)}</td>
                            <td className="p-3 text-right font-bold text-gray-900 whitespace-nowrap">{formatSEK(t.total_expeditions_intakter)}</td>
                            <td className="p-3 text-right font-bold text-gray-900 whitespace-nowrap">{formatSEK(t.total_provision_bf)}</td>
                            <td className="p-3 text-right font-bold text-gray-900 whitespace-nowrap">{formatSEK(t.total_moms_byggfynd_redovisning ?? bfMomsSum)}</td>
                            <td className="p-3 text-right font-bold text-[#003B73] whitespace-nowrap">{formatSEK(t.total_utbetalning_butiker)}</td>
                            <td />
                          </tr>
                        </tfoot>
                      );
                    })()}
                  </table>
                </div>
                <p className="px-5 py-3 text-xs text-gray-500 border-t border-gray-100 bg-gray-50/80">
                  Per order: använd listan <strong>Ordrar i vald period</strong> ovan, öppna en butik med <strong>Ordrar</strong>, eller ladda ner PDF/CSV. Fliken <strong>Att betala ut</strong> visar endast obetalda ordrar.
                </p>
              </div>
            )}

            <OrderTransactionDialog
              orderId={invoiceTxOrderId}
              open={Boolean(invoiceTxOrderId)}
              onOpenChange={(v) => { if (!v) setInvoiceTxOrderId(null); }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default InvoiceDataPage;

